import {
  TENDER_NO_BID_REASON_LABELS,
  TENDER_NOTICE_TYPE_LABELS,
  TENDER_NO_BID_REASONS,
  TENDER_STATUS_HELP,
  formatDate,
  formatNumber,
  type TenderDto,
  type TenderStatus,
  type UserDto,
} from '@nova/shared';
import { useQuery } from '@tanstack/react-query';
import { CalendarClock, ExternalLink, HelpCircle, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../components/Button.js';
import { Drawer } from '../../components/Drawer.js';
import { SkeletonPanel } from '../../components/Skeleton.js';
import { api } from '../../lib/api.js';
import { cn } from '../../lib/cn.js';
import { TenderStatusBadge } from './TenderStatusBadge.js';
import { useAssignTender, useTender, useUpdateTenderStatus } from './useSales.js';

const STATUS_LABEL_SHORT: Record<TenderStatus, string> = {
  NY: 'Ny',
  VURDERES: 'Sett til vurdering',
  BID: 'Bid',
  NO_BID: 'No-bid',
  UNDER_ARBEID: 'Under arbeid',
  LEVERT: 'Levert',
  VUNNET: 'Vunnet',
  TAPT: 'Tapt',
  AVLYST: 'Avlyst',
};

export function TenderDetail({ id, onClose }: { id: string | null; onClose: () => void }) {
  const { data: tender, isLoading } = useTender(id);
  return (
    <Drawer
      open={Boolean(id)}
      onClose={onClose}
      title={tender ? tender.title : 'Anbud'}
      subtitle={tender ? tender.buyerName : undefined}
    >
      {isLoading || !tender ? <SkeletonPanel lines={6} /> : <TenderBody tender={tender} />}
    </Drawer>
  );
}

function TenderBody({ tender }: { tender: TenderDto }) {
  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<UserDto[]>('/users'),
  });
  const changeStatus = useUpdateTenderStatus(tender.id);
  const assign = useAssignTender(tender.id);

  const [noBidOpen, setNoBidOpen] = useState(false);
  const [noBidReason, setNoBidReason] = useState<string>(TENDER_NO_BID_REASONS[0]);
  const [noBidNote, setNoBidNote] = useState('');

  function move(status: TenderStatus) {
    if (status === 'NO_BID') {
      setNoBidOpen(true);
      return;
    }
    changeStatus.mutate({ status });
  }

  return (
    <div className="space-y-5">
      {/* Status + meta */}
      <div className="flex flex-wrap items-center gap-2">
        <TenderStatusBadge status={tender.status} />
        <span className="text-ink-soft rounded-full border border-hairline px-2.5 py-1 text-xs">
          {TENDER_NOTICE_TYPE_LABELS[tender.noticeType]}
        </span>
        {tender.isBelowThreshold && (
          <span className="text-warning border-warning/25 bg-warning/[0.08] rounded-full border px-2.5 py-1 text-xs">
            Under terskelverdi
          </span>
        )}
        {tender.isSeed && (
          <span className="text-ink-soft border-hairline rounded-full border px-2.5 py-1 text-[11px]">
            demo
          </span>
        )}
      </div>

      <p className="text-ink-soft text-xs">{TENDER_STATUS_HELP[tender.status]}</p>

      {/* Key facts */}
      <dl className="grid grid-cols-2 gap-3 text-sm">
        <Fact label="Oppdragsgiver" value={tender.buyerName} />
        <Fact label="Org.nr" value={tender.buyerOrgNr ?? '—'} />
        <Fact
          label="Tilbudsfrist"
          value={tender.deadline ? formatDate(tender.deadline) : 'Ikke oppgitt'}
        />
        <Fact
          label="Spørsmålsfrist"
          value={tender.questionDeadline ? formatDate(tender.questionDeadline) : 'Ikke oppgitt'}
        />
        <Fact
          label="Estimert verdi"
          value={
            tender.estimatedValue
              ? `${formatNumber(tender.estimatedValue)} ${tender.currency ?? 'NOK'} (demo)`
              : 'Ikke oppgitt'
          }
        />
        <Fact label="Publisert" value={formatDate(tender.publishedAt)} />
      </dl>

      {tender.description && (
        <p className="text-ink text-sm leading-relaxed">{tender.description}</p>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <a
          href={tender.url}
          target="_blank"
          rel="noreferrer noopener"
          className="text-accent inline-flex items-center gap-1 text-xs"
        >
          Se på Doffin <ExternalLink size={12} aria-hidden />
        </a>
        {tender.cpvCodes.length > 0 && (
          <span className="text-ink-soft text-xs">CPV: {tender.cpvCodes.join(', ')}</span>
        )}
      </div>

      {/* Ansvarlig */}
      <div>
        <p className="label-micro text-ink-soft mb-1.5">Ansvarlig selger</p>
        <select
          className="border-hairline bg-base/40 text-ink w-full rounded-lg border px-3 py-2 text-sm"
          value={tender.ownerUserId ?? ''}
          onChange={(e) => assign.mutate(e.target.value || null)}
          disabled={assign.isPending}
        >
          <option value="">Ingen</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </select>
      </div>

      {/* Relevans / treff */}
      <section className="space-y-2">
        <p className="label-micro text-ink-soft flex items-center gap-1.5">
          <Sparkles size={13} aria-hidden /> Relevans per profil
        </p>
        {tender.matches.length === 0 ? (
          <p className="text-ink-soft text-xs">Ingen profiler traff denne kunngjøringen.</p>
        ) : (
          tender.matches.map((m) => (
            <div key={m.id} className="border-hairline bg-base/30 rounded-lg border p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-ink text-sm">{m.profileName}</span>
                <ScorePill score={m.score} />
              </div>
              <p className="text-ink-soft mt-1 text-xs leading-relaxed">{m.rationale}</p>
              {m.matchedOn.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {m.matchedOn.map((c) => (
                    <span
                      key={c}
                      className="text-ink-soft border-hairline rounded-full border px-2 py-0.5 text-[11px]"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </section>

      {/* No-bid årsak (registrert) */}
      {tender.status === 'NO_BID' && tender.noBidReason && (
        <p className="text-ink-soft text-xs">
          No-bid-årsak: <span className="text-ink">{TENDER_NO_BID_REASON_LABELS[tender.noBidReason]}</span>
          {tender.noBidNote ? ` — ${tender.noBidNote}` : ''}
        </p>
      )}

      {/* Statushandlinger */}
      {tender.allowedNextStatuses.length > 0 && (
        <section className="border-hairline space-y-2 border-t pt-4">
          <p className="label-micro text-ink-soft">Neste steg</p>
          <div className="flex flex-wrap gap-2">
            {tender.allowedNextStatuses.map((s) => (
              <Button
                key={s}
                size="sm"
                variant={s === 'BID' ? 'primary' : s === 'AVLYST' || s === 'NO_BID' ? 'secondary' : 'secondary'}
                loading={changeStatus.isPending}
                onClick={() => move(s)}
              >
                {STATUS_LABEL_SHORT[s]}
              </Button>
            ))}
          </div>

          {noBidOpen && (
            <div className="border-hairline bg-base/30 mt-2 space-y-2 rounded-lg border p-3">
              <p className="text-ink flex items-center gap-1.5 text-sm">
                <HelpCircle size={14} aria-hidden /> Hvorfor gir vi ikke tilbud?
              </p>
              <select
                className="border-hairline bg-base/40 text-ink w-full rounded-lg border px-3 py-2 text-sm"
                value={noBidReason}
                onChange={(e) => setNoBidReason(e.target.value)}
              >
                {TENDER_NO_BID_REASONS.map((r) => (
                  <option key={r} value={r}>
                    {TENDER_NO_BID_REASON_LABELS[r]}
                  </option>
                ))}
              </select>
              <textarea
                className="border-hairline bg-base/40 text-ink w-full rounded-lg border px-3 py-2 text-sm"
                rows={2}
                placeholder="Utdyp gjerne (valgfritt)"
                value={noBidNote}
                onChange={(e) => setNoBidNote(e.target.value)}
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="primary"
                  loading={changeStatus.isPending}
                  onClick={() =>
                    changeStatus.mutate(
                      { status: 'NO_BID', noBidReason, noBidNote: noBidNote || null },
                      { onSuccess: () => setNoBidOpen(false) },
                    )
                  }
                >
                  Registrer no-bid
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setNoBidOpen(false)}>
                  Avbryt
                </Button>
              </div>
            </div>
          )}

          {changeStatus.isError && (
            <p className="text-danger text-xs">{(changeStatus.error as Error).message}</p>
          )}
        </section>
      )}

      {(tender.deadline || tender.questionDeadline) && (
        <p className="text-ink-soft flex items-center gap-1.5 text-[11px]">
          <CalendarClock size={12} aria-hidden />
          Anbudsspeideren purrer ansvarlig før fristene.
        </p>
      )}
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="label-micro text-ink-soft">{label}</dt>
      <dd className="text-ink mt-0.5">{value}</dd>
    </div>
  );
}

function ScorePill({ score }: { score: number }) {
  const tone =
    score >= 70
      ? 'bg-success/15 text-success border-success/30'
      : score >= 45
        ? 'bg-accent/12 text-accent border-accent/25'
        : 'bg-ink-soft/10 text-ink-soft border-ink-soft/20';
  return (
    <span className={cn('rounded-full border px-2 py-0.5 text-[11px] font-medium', tone)}>
      {score}/100
    </span>
  );
}
